import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, X, Loader2, Calendar, Camera, MapPin, Search, FileText, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import heic2any from "heic2any";
import { API, useAuth } from "@/App";
import { authHeaders, getAuthToken } from "@/lib/firebase";
import TopNav from "@/components/TopNav";
import HostLayout from "@/components/host/HostLayout";
import SportPicker from "@/components/SportPicker";
import { useDraft } from "@/hooks/useDraft";

const UK_CITIES = ["London", "Manchester", "Birmingham", "Liverpool", "Edinburgh", "Bristol", "Leeds", "Glasgow"];
const EU_CITIES = ["Berlin", "Amsterdam", "Paris"];
const currencyForCity = (city) => UK_CITIES.includes(city) ? "GBP" : EU_CITIES.includes(city) ? "EUR" : "USD";
const currencySymbol = (city) => UK_CITIES.includes(city) ? "£" : EU_CITIES.includes(city) ? "€" : "$";

const sportImages = {
  basketball: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800",
  football: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800",
  padel: "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800",
  volleyball: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=800",
};

const geocodeAddress = async (address, city) => {
  try {
    const query = encodeURIComponent(`${address}, ${city}`);
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`, {
      headers: { 'User-Agent': 'Pouls App' }
    });
    const data = await res.json();
    if (data && data.length > 0) {
      return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon), displayName: data[0].display_name };
    }
    return null;
  } catch (e) {
    return null;
  }
};

const emptyForm = {
  sport: "basketball",
  title: "",
  description: "",
  date: "",
  time_start: "",
  time_end: "",
  location_name: "",
  city: "",
  address: "",
  latitude: 0,
  longitude: 0,
  max_players: 10,
  price: 0,
  currency: "GBP",
  image_url: sportImages.basketball,
  host_name: "",
  host_image_url: "",
  host_contact: "",
  meeting_info: "",
  skill_level: "all",
};

const CreateGamePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;
  const [formData, setFormData] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const { saveDraft, discardDraft, saving: savingDraft, autoSaveStatus } = useDraft("game", {
    onLoad: (payload) => setFormData(prev => ({ ...emptyForm, ...payload })),
    autoSaveData: formData,
    autoSaveEnabled: !isEditMode, // don't auto-save drafts while editing an existing event
  });

  // Prefill the form when editing an existing event
  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/games/${editId}`, { headers });
        if (!res.ok) {
          toast.error("Couldn't load event to edit");
          navigate("/host");
          return;
        }
        const g = await res.json();
        if (cancelled) return;
        setFormData({
          sport: g.sport || "padel",
          title: g.title || "",
          description: g.description || "",
          city: g.city || "London",
          location_name: g.location_name || "",
          address: g.address || "",
          latitude: g.latitude || 0,
          longitude: g.longitude || 0,
          date: g.date ? String(g.date).slice(0, 10) : "",
          time_start: g.time_start || "",
          time_end: g.time_end || "",
          max_players: g.max_players || 8,
          price: g.price || 0,
          skill_level: g.skill_level || "all",
          image_url: g.image_url || "",
          host_contact: g.host_contact || "",
          meeting_info: g.meeting_info || "",
        });
        if (g.image_url) setImagePreview(g.image_url);
      } catch (_) {
        toast.error("Network error");
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEditMode, navigate]);

  // Prefill date/time from the Schedule drag-to-create flow
  useEffect(() => {
    if (isEditMode) return;
    const d = searchParams.get("date"), s = searchParams.get("start"), e = searchParams.get("end");
    if (!d && !s && !e) return;
    setFormData((prev) => ({ ...prev, ...(d ? { date: d } : {}), ...(s ? { time_start: s } : {}), ...(e ? { time_end: e } : {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageUpload = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    // HEIC → JPEG for iPhones
    if (file.type === "image/heic" || file.type === "image/heif" || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif")) {
      try {
        const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        file = new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
      } catch (_) {
        toast.error("Failed to convert iPhone photo. Try taking a screenshot instead.");
        return;
      }
    }
    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);
    try {
      const token = await getAuthToken();
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/upload/game-image`);
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) resolve(data);
            else reject(new Error(data.detail || "Upload failed"));
          } catch { reject(new Error("Upload failed")); }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        const body = new FormData();
        body.append("file", file);
        xhr.send(body);
      });
      const imageUrl = `${API}/files/${result.storage_path}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreate = async () => {
    if (creating) return;
    if (!formData.title || !formData.date || !formData.time_start || !formData.time_end || !formData.city || !formData.location_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...formData,
        host_name: formData.host_name || user?.name || "Host",
        host_image_url: formData.host_image_url || user?.picture || "",
        currency: currencyForCity(formData.city),
        date: new Date(formData.date).toISOString(),
      };
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const url = isEditMode ? `${API}/games/${editId}` : `${API}/games`;
      const method = isEditMode ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        const game = await res.json();
        if (isEditMode) {
          toast.success("Game updated");
          navigate(`/host/manage/game/${game.game_id || editId}`);
        } else {
          toast.success("Game created! Share it with your friends");
          await discardDraft();
          navigate(`/game/${game.game_id}`);
        }
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || `Failed to ${isEditMode ? "update" : "create"} game`);
      }
    } catch (e) {
      toast.error(`Failed to ${isEditMode ? "update" : "create"} game`);
    } finally {
      setCreating(false);
    }
  };

  const fromHost = searchParams.get("from") === "host";
  const backTo = isEditMode ? `/host/manage/game/${editId}` : (fromHost ? "/host/events" : "/profile");

  const content = (
    <>
      <div data-testid="create-game-page">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="create-game-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl flex items-center gap-2">
            <Calendar className="w-7 h-7 text-primary" /> {isEditMode ? "Edit session" : "Create a single session"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isEditMode ? "Update the details below. Attendees will keep their spots." : "A one-off class, session or appointment on a specific date."}
          </p>
        </div>

        {loadingEdit && (
          <div className="py-8 flex items-center justify-center gap-2 text-muted-foreground" data-testid="edit-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading event…
          </div>
        )}

        <div className="space-y-5">
          <div>
            <Label>Activity type *</Label>
            <SportPicker
              value={formData.sport}
              onChange={(v) => setFormData(prev => ({ ...prev, sport: v, image_url: sportImages[v] || prev.image_url }))}
              className="bg-muted/50"
              testid="game-sport-select"
            />
          </div>

          <div>
            <Label>Title *</Label>
            <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g. Morning Reformer Flow" className="bg-muted/50" data-testid="game-title-input" />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Tell people what to expect..." className="bg-muted/50" rows={2} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="bg-muted/50" data-testid="game-date-input" />
            </div>
            <div>
              <Label>Start *</Label>
              <Input type="time" value={formData.time_start}
                onChange={(e) => setFormData(prev => ({ ...prev, time_start: e.target.value }))}
                className="bg-muted/50" data-testid="game-start-input" />
            </div>
            <div>
              <Label>End *</Label>
              <Input type="time" value={formData.time_end}
                onChange={(e) => setFormData(prev => ({ ...prev, time_end: e.target.value }))}
                className="bg-muted/50" data-testid="game-end-input" />
            </div>
          </div>

          <div>
            <Label>City *</Label>
            <Select value={formData.city} onValueChange={(v) => setFormData(prev => ({ ...prev, city: v, currency: currencyForCity(v) }))}>
              <SelectTrigger className="bg-muted/50" data-testid="game-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent>
                {[...UK_CITIES, "New York", "Los Angeles", "Chicago", "Miami", ...EU_CITIES].map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Venue name *</Label>
            <Input value={formData.location_name}
              onChange={(e) => setFormData(prev => ({ ...prev, location_name: e.target.value }))}
              placeholder="e.g. Brixton Recreation Centre" className="bg-muted/50" data-testid="game-venue-input" />
          </div>

          <div>
            <Label>Address *</Label>
            <div className="flex gap-2">
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value, latitude: 0, longitude: 0 }))}
                placeholder="Full address (e.g. 27 Brixton Station Rd, London SW9 8QQ)"
                className="bg-muted/50 flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!formData.address) {
                    toast.error("Enter an address first");
                    return;
                  }
                  toast.loading("Looking up address...", { id: "geocode" });
                  const result = await geocodeAddress(formData.address, formData.city);
                  if (result) {
                    setFormData(prev => ({ ...prev, latitude: result.latitude, longitude: result.longitude }));
                    toast.success("Location found!", { id: "geocode" });
                  } else {
                    toast.error("Couldn't find that address. Try being more specific.", { id: "geocode" });
                  }
                }}
                className="shrink-0"
              >
                <Search className="w-4 h-4 mr-1" /> Find
              </Button>
            </div>
            {formData.latitude !== 0 && formData.longitude !== 0 && (
              <div className="mt-2 rounded-lg overflow-hidden border border-border">
                <iframe
                  title="Location Preview"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${formData.longitude - 0.003},${formData.latitude - 0.002},${formData.longitude + 0.003},${formData.latitude + 0.002}&layer=mapnik&marker=${formData.latitude},${formData.longitude}`}
                  className="w-full h-28"
                  style={{ border: 0 }}
                />
                <p className="text-xs text-green-600 px-2 py-1.5 bg-green-50 dark:bg-green-950/30 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Location verified - this is where the pin will show
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Capacity *</Label>
              <Input type="number" min="2" max="50" value={formData.max_players}
                onChange={(e) => setFormData(prev => ({ ...prev, max_players: parseInt(e.target.value) || 2 }))}
                className="bg-muted/50" data-testid="game-players-input" />
            </div>
            <div>
              <Label>Price ({currencySymbol(formData.city)})</Label>
              <Input type="number" min="0" step="0.5" value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                className="bg-muted/50" data-testid="game-price-input" />
            </div>
          </div>

          <div>
            <Label>Level</Label>
            <Select value={formData.skill_level} onValueChange={(v) => setFormData(prev => ({ ...prev, skill_level: v }))}>
              <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border border-border rounded-lg overflow-hidden bg-muted/20">
            <button
              type="button"
              onClick={() => setImageOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
              data-testid="cover-image-toggle"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <Camera className="w-4 h-4 text-muted-foreground" /> Cover image
                <span className="text-xs text-muted-foreground font-normal">· optional</span>
              </span>
              <div className="flex items-center gap-2">
                {!imageOpen && (
                  <img src={imagePreview || formData.image_url} alt="" className="w-9 h-9 rounded object-cover border border-border" />
                )}
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${imageOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {imageOpen && (
              <div className="px-4 pb-4">
                <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30" style={{ aspectRatio: "16/9" }}>
                  <img src={imagePreview || formData.image_url} alt="Game preview" className="w-full h-full object-cover" />
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                  <label
                    htmlFor="game-image-upload"
                    className="absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 hover:bg-black/90 text-white text-xs font-medium cursor-pointer transition-colors"
                    data-testid="upload-image-btn"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    {uploadingImage ? "Uploading..." : "Upload image"}
                  </label>
                  <input
                    id="game-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    data-testid="game-image-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Auto-cropped to fit. Or use the default image.</p>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="host_contact">Your contact (optional)</Label>
            <Input
              id="host_contact"
              type="text"
              placeholder="Phone, WhatsApp, Instagram, etc."
              value={formData.host_contact}
              onChange={(e) => setFormData(prev => ({ ...prev, host_contact: e.target.value }))}
              className="bg-muted/50"
              data-testid="host-contact-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Visible to guests who booked.</p>
          </div>

          <div>
            <Label htmlFor="meeting_info">Meeting info (optional)</Label>
            <Textarea
              id="meeting_info"
              placeholder="e.g. We meet at the entrance gate 10 min early, bring a light + dark shirt, parking on Maple St."
              value={formData.meeting_info}
              onChange={(e) => setFormData(prev => ({ ...prev, meeting_info: e.target.value }))}
              className="bg-muted/50 min-h-[80px]"
              rows={3}
              data-testid="meeting-info-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Where to meet, what to bring, how to find you. Visible to guests who booked.</p>
          </div>
        </div>
      </div>

      <div className={`fixed bottom-0 inset-x-0 border-t border-border bg-background/95 backdrop-blur z-40 ${fromHost ? "lg:left-60" : ""}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => navigate(backTo)} className="gap-2">
            <X className="w-4 h-4" /> Cancel
          </Button>
          <div className="flex items-center gap-2">
            {!isEditMode && autoSaveStatus !== "idle" && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="autosave-indicator">
                {autoSaveStatus === "saving" ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Auto-saving…</>
                ) : (
                  <>Draft saved</>
                )}
              </span>
            )}
            {!isEditMode && (
              <Button
                variant="outline"
                onClick={() => saveDraft(formData)}
                disabled={savingDraft || creating}
                className="gap-2"
                data-testid="save-game-draft-btn"
              >
                {savingDraft ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><FileText className="w-4 h-4" /> Save draft</>}
              </Button>
            )}
            <Button onClick={handleCreate} disabled={creating || loadingEdit} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="submit-game-btn">
              {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> {isEditMode ? "Saving…" : "Creating..."}</> : <><Save className="w-4 h-4" /> {isEditMode ? "Save changes" : "Create game"}</>}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return fromHost
    ? <HostLayout><div className="max-w-3xl mx-auto pb-28">{content}</div></HostLayout>
    : <div className="min-h-screen bg-background"><TopNav /><main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-32">{content}</main></div>;
};

export default CreateGamePage;
